import classNames from "classnames";
import * as React from "react";
import { Button, Menu, Wrapper } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";

import EditMcsForm, { FormValues } from "./EditMcsForm";
import style from "./mcs.scss";
import ConfirmationModal from "components/confirmation/ConfirmationModal";
import { CustomToastCloseButton } from "components/icons/CustomToastCloseButton";
import KebabIcon from "components/icons/KebabIcon";
import { SuccessIcon } from "components/icons/SuccessIcon";
import { subpageContext } from "components/layout/ApplicationLayout";
import layoutStyle from "components/layout/layout.scss";
import { MANAGEMENT_CONSOLE_LICENSE } from "components/licenses/bundles";
import {
    isAutopilotLicense,
    isFeatureLicense,
    isIbrFeature,
    isSubscriptionLicense,
    RESTRICTED_ENTITLEMENT_LICENSE_TYPES,
} from "components/licenses/common";
import AddEntitlementWrapper from "components/licenses/entitlements/add-entitlement/AddEntitlementWrapper";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import { isTokenLicense } from "components/tenants/add-tenant/AddTenantForm";
import Tooltip from "components/tooltip/Tooltip";
import { AUTH_EMS_CREATE_ENTITLEMENT, AUTH_MC_CREATE, AUTH_MC_EDIT, AUTH_MC_UNREGISTER } from "domain/authority";
import { Container, EmsConfiguration, EntitlementState, LicenseData, MIN_DATE } from "domain/licenses";
import { McsTableData } from "domain/mc";
import { hybridMcService } from "services/hybrid-mc/HybridMcService";
import { ProductRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie, isCurrentUserInternal, isInTenantAccess } from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { trimToUndefined } from "utils/commonFunctions";
import { isExpired } from "utils/format";
import { Logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

import infoBadge from "assets/images/icons/product-icons/infoBadge.svg";

import testIds from "testIds.json";

const LOGGER = new Logger("McsKebabMenu");

interface Result {
    title: string;
    message: string;
}

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

const MENU_ACTIONS = {
    EDIT: "edit",
    UNREGISTER: "unregister",
    RESET: "reset",
    DISABLE: "disable",
    ENABLE: "enable",
    DELIVERY: "delivery",
} as const;

const MC_STATUS = {
    ACTIVE: "ACTIVE",
    DISABLED: "DISABLED",
    HALTED: "HALTED",
} as const;

export function allowHlUpdate(
    emsConfiguration: EmsConfiguration,
    allContainers: Container[],
    mcContainer: string | undefined
) {
    if (!mcContainer || !emsConfiguration.hlEntitlements) {
        return false;
    }
    const foundContainer = findContainer(allContainers, mcContainer);
    // The most common length for SL containers is 18 characters. But there were SL containers that had 17 characters.
    // The most common length for HL containers is 9 characters.
    // So we will go with something in the middle and assume that SL containers are at least 14 characters long.
    const slContainerLength = 14;

    if (!foundContainer) {
        return mcContainer.length < slContainerLength;
    }
    return foundContainer.type.startsWith("HL");
}

export function allowSlUpdate(
    emsConfiguration: EmsConfiguration,
    mcContainer: string | undefined,
    allContainers: Container[]
): boolean {
    if (!mcContainer || !emsConfiguration.slEntitlements) {
        return false;
    }
    const foundContainer = findContainer(allContainers, mcContainer);
    if (!foundContainer) {
        return false;
    }
    return foundContainer.type.startsWith("SL");
}

export function allowNewSoftlock(mcContainer: string | undefined, availableActivations: number, withSl: boolean) {
    if (!mcContainer) {
        return availableActivations > 0 && withSl;
    }
    return false;
}

function findContainer(containers: Container[], containerId: string) {
    return containers.find((each) => each.containerId === containerId);
}

function allowDeliveryBasedOnContainer(
    emsConfiguration: EmsConfiguration,
    allContainers: Container[],
    mcContainer: string | undefined
) {
    if (!mcContainer) {
        return allowNewSoftlock(mcContainer, emsConfiguration.availableSlActivations, emsConfiguration.slEntitlements);
    }
    if (allowHlUpdate(emsConfiguration, allContainers, mcContainer)) {
        return true;
    }

    return allowSlUpdate(emsConfiguration, mcContainer, allContainers);
}

const McsKebabMenu = (
    props: ConnectedProps<typeof connector> & {
        name: string;
        description: string;
        uuid: string;
        status: string;
        tableState: McsTableData[];
        onMcChange: () => void;
        isBottomElement?: boolean;
        emsConfiguration: EmsConfiguration;
        availableLicenses: LicenseData[];
        allContainers: Container[];
        mcVersion?: string;
        mcContainer?: string;
        entitlementState?: EntitlementState;
        tokenRates?: ProductRateList[];
        preselectedProducts?: string[];
        autoOpenCreateEntitlement?: boolean;
        totalMcsCount?: number;
        isLoadingEmsData?: boolean;
    }
): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [editMcsFormVisible, setEditMcsFormVisible] = React.useState(false);
    const [disableMcsConfirmationModalVisible, setDisableMcsConfirmationModalVisible] = React.useState(false);
    const [unregisterMcsConfirmationModalVisible, setUnregisterMcsConfirmationModalVisible] = React.useState(false);
    const [resetMcsConfirmationWindowVisible, setResetMcsConfirmationWindowVisible] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [resultVisible, setResultVisible] = React.useState(false);
    const minimumMcVersionForEntitlements = "6.2.0";
    const context = React.useContext(subpageContext);
    const history = useHistory();

    function hideEntitlementForm() {
        context?.setSubpage({
            content: undefined,
        });
    }
    const showResult = (resultToShow: Result) => {
        setResult(resultToShow);
        setResultVisible(true);
    };
    const hideResult = () => {
        setResultVisible(false);
    };

    const mcLicense = props.availableLicenses.find((license) => license.type === MANAGEMENT_CONSOLE_LICENSE);
    const expiration = mcLicense?.expirationDate || MIN_DATE.toISOString();
    const licenseExpired = !expiration || isExpired(expiration);

    const isActive = props.status === MC_STATUS.ACTIVE;
    const isActiveOrHalted = isActive || props.status === MC_STATUS.HALTED;

    React.useEffect(() => {
        return () => {
            abortControllers.forEach((abortController) => abortController.abort());
        };
    }, []);

    const handleResetClick = () => {
        setIsLoading(true);
        usageStatisticsService.sendEvent({
            category: Category.MCS,
            action: Action.RESET_MC,
        });
        const abortController = new AbortController();
        abortControllers.push(abortController);
        hybridMcService
            .resetMc(props.uuid, abortController)
            .then((response) => {
                return navigator.clipboard.writeText(response).then(() => {
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("Mcs.copyResetCodeSuccess")}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                });
            })
            .catch((error) => {
                if (!abortController?.signal.aborted) {
                    LOGGER.error("Couldn't copy to clipboard", error);
                }
            })
            .finally(() => {
                setResetMcsConfirmationWindowVisible(false);
                setIsLoading(false);
            });
    };
    const hideEditMcsForm = () => {
        setEditMcsFormVisible(false);
    };

    const editMc = async ({ uuid, name: newName, description }: FormValues): Promise<void> => {
        setIsLoading(true);
        const abortController = new AbortController();
        abortControllers.push(abortController);

        const effectiveDescription = trimToUndefined(description);
        newName = newName.trim();
        const effectiveNewName = props.name === newName ? props.name : newName;
        hybridMcService
            .editMc(uuid, effectiveNewName, effectiveDescription, abortController)
            .then(() => {
                if (!abortController.signal.aborted) {
                    toast(
                        <SuccessIcon
                            successClass={layoutStyle.customToastSuccessIcon}
                            color={theme.contentBackgroundColor}
                            text={t("Mcs.edit.successMessage", { name: newName })}
                        />,
                        {
                            closeButton: (closeToastProps) => (
                                <CustomToastCloseButton
                                    closeToast={{ ...closeToastProps }}
                                    color={theme.iconFillColor}
                                />
                            ),
                            className: layoutStyle.customToastSuccessMessage,
                        }
                    );
                    props.onMcChange();
                }
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    showResult({
                        title: t("ManageRoleForm.edit.failure.title"),
                        message: t("Mcs.edit.failureMessage", { name: props.name }),
                    });
                    LOGGER.error("MC edit failed ", error);
                }
            })
            .finally(() => {
                setIsLoading(false);
                setEditMcsFormVisible(false);
            });
    };

    const toggleDisableMcConfirmationModal = () => {
        setDisableMcsConfirmationModalVisible((prev) => !prev);
    };

    const toggleUnregisterMcConfirmationModal = () => {
        setUnregisterMcsConfirmationModalVisible((prev) => !prev);
    };

    const toggleResetMcConfirmationModal = () => {
        setResetMcsConfirmationWindowVisible((prev) => !prev);
    };

    const toggleMcActiveStatus = (action: "enable" | "disable") => {
        setIsLoading(true);
        usageStatisticsService.sendEvent({
            category: Category.MCS,
            action: action === "enable" ? Action.ENABLE_MC : Action.DISABLE_MC,
        });

        const abortController = new AbortController();
        abortControllers.push(abortController);

        hybridMcService
            .toggleMcActiveStatus(props.uuid, action, abortController.signal)
            .catch((error) => {
                LOGGER.error(`Error trying to ${action} MC`, error);
                if (!abortController.signal.aborted) {
                    showResult({
                        title: t("Mcs.toggleActiveStatusFailureTitle", { action: action }),
                        message: t("Mcs.toggleActiveStatusFailureMessage", { action: action, name: props.name }),
                    });
                }
            })
            .finally(() => {
                setDisableMcsConfirmationModalVisible(false);
                setIsLoading(false);
                props.onMcChange();
            });
    };

    const unregisterMc = () => {
        setIsLoading(true);

        usageStatisticsService.sendEvent({
            category: Category.MCS,
            action: Action.UNREGISTER_MC,
        });

        const abortController = new AbortController();
        abortControllers.push(abortController);

        hybridMcService
            .unregisterMc(props.uuid, abortController.signal)
            .then(() => {
                toast(
                    <SuccessIcon
                        successClass={layoutStyle.customToastSuccessIcon}
                        color={theme.contentBackgroundColor}
                        text={t("Mcs.unregisterMcSuccess", { name: props.name })}
                    />,
                    {
                        closeButton: (closeToastProps) => (
                            <CustomToastCloseButton closeToast={{ ...closeToastProps }} color={theme.iconFillColor} />
                        ),
                        className: layoutStyle.customToastSuccessMessage,
                    }
                );
            })
            .catch((error) => {
                LOGGER.error(`Error trying to unregister MC`, error);
                if (!abortController.signal.aborted) {
                    showResult({
                        title: t("Mcs.unregisterFailureTitle"),
                        message: t("Mcs.unregisterFailureMessage", { name: props.name }),
                    });
                }
            })
            .finally(() => {
                setUnregisterMcsConfirmationModalVisible(false);
                setIsLoading(false);
                props.onMcChange();
            });
    };

    const handleSelection = (value: string) => {
        switch (value) {
            case MENU_ACTIONS.EDIT:
                setEditMcsFormVisible(true);
                usageStatisticsService.sendEvent({
                    category: Category.MCS,
                    action: Action.EDIT_MC,
                });
                break;
            case MENU_ACTIONS.UNREGISTER:
                toggleUnregisterMcConfirmationModal();
                break;
            case MENU_ACTIONS.RESET:
                toggleResetMcConfirmationModal();
                break;
            case MENU_ACTIONS.DISABLE:
                toggleDisableMcConfirmationModal();
                break;
            case MENU_ACTIONS.ENABLE:
                toggleMcActiveStatus(value);
                break;
            case MENU_ACTIONS.DELIVERY:
                openEntitlementCreationDialog();
                break;
            default:
                LOGGER.error("Unknown menu action", value);
                break;
        }
    };

    function allowBasedOnEntitlementState() {
        if (!props.entitlementState) {
            return true;
        }
        return [EntitlementState.ACTIVATED, EntitlementState.FAILED, EntitlementState.MANUALLY_ACTIVATED].includes(
            props.entitlementState
        );
    }

    function hideEntitlementCreationAction(): boolean {
        const hasContainer = !!props.mcContainer;
        const anyOperationAllowed =
            allowNewSoftlock(
                props.mcContainer,
                props.emsConfiguration.availableSlActivations,
                props.emsConfiguration.slEntitlements
            ) ||
            allowHlUpdate(props.emsConfiguration, props.allContainers, props.mcContainer) ||
            allowSlUpdate(props.emsConfiguration, props.mcContainer, props.allContainers);

        const showDisabledForUnmatchedContainer = hasContainer && !anyOperationAllowed;

        return (
            !userSessionService.userHasAnyAuthority([AUTH_EMS_CREATE_ENTITLEMENT]) ||
            hasTenantCookie() ||
            (!props.isLoadingEmsData && !props.emsConfiguration.emsId) ||
            !isActive ||
            !(props.mcVersion && props.mcVersion >= minimumMcVersionForEntitlements) ||
            (!props.isLoadingEmsData &&
                !props.emsConfiguration.hlEntitlements &&
                !props.emsConfiguration.slEntitlements) ||
            (!props.isLoadingEmsData && areLicensesInvalid()) ||
            (!anyOperationAllowed && !showDisabledForUnmatchedContainer)
        );
    }

    function areLicensesInvalid(): boolean {
        // it's not subscription license
        // it's not feature license, unless it's WF feature license
        const filtered = props.availableLicenses.filter(
            (license) =>
                (isFeatureLicense(license.type) && isIbrFeature(license.type)) ||
                (!isSubscriptionLicense(license.type) &&
                    !isFeatureLicense(license.type) &&
                    !isAutopilotLicense(license.type)) ||
                isTokenLicense(license.type) ||
                !RESTRICTED_ENTITLEMENT_LICENSE_TYPES.includes(license.type)
        );
        return filtered.every((license) => isExpired(license.expirationDate) || license.available <= 0);
    }

    function disableEntitlementCreation(): boolean {
        return (
            !allowBasedOnEntitlementState() ||
            !allowDeliveryBasedOnContainer(props.emsConfiguration, props.allContainers, props.mcContainer)
        );
    }

    function openEntitlementCreationDialog() {
        context?.setSubpage({
            content: (
                <AddEntitlementWrapper
                    emsConfiguration={props.emsConfiguration}
                    availableLicenses={props.availableLicenses}
                    allContainers={props.allContainers}
                    modalVisibility={true}
                    onUpdate={props.onMcChange}
                    setModalVisibility={hideEntitlementForm}
                    mcUuid={props.uuid}
                    mcContainer={props.mcContainer}
                    tokenRates={props.tokenRates}
                    mcName={props.name}
                    preselectedProducts={props.preselectedProducts}
                />
            ),
            isModal: false,
        });
    }

    const editVisible = userSessionService.userHasAllAuthorities([AUTH_MC_EDIT]);
    const createVisible = userSessionService.userHasAllAuthorities([AUTH_MC_CREATE]);
    const unregisterVisible = userSessionService.userHasAllAuthorities([AUTH_MC_UNREGISTER]);
    const entitlementMenuItemId = "entitlementMenuItem";

    React.useEffect(() => {
        if (!hideEntitlementCreationAction()) {
            // This is for rebinding all tooltips - fixes displaying the tooltips for Access Tenant icon and Edit Tenant icon
            // Those two tooltips only exist after they've become visible and by default ReactTooltip doesn't notice that
            // they've appeared and thus there's no tooltip without rebinding
            ReactTooltip.rebuild();
        }
    }, [hideEntitlementCreationAction()]);

    React.useEffect(() => {
        if (
            props.autoOpenCreateEntitlement &&
            props.totalMcsCount === 1 &&
            !hideEntitlementCreationAction() &&
            !disableEntitlementCreation()
        ) {
            openEntitlementCreationDialog();
            history.replace(history.location.pathname, { autoOpenCreateEntitlement: false });
        }
    }, [props.autoOpenCreateEntitlement]);

    return (
        <div
            className={classNames(style.tenantIcon, {
                [style.kebabMenuAtBottom]: props.isBottomElement,
                [style.kebabMenuAtTop]: !props.isBottomElement,
            })}
        >
            <Wrapper
                className={classNames(style.kebabIconContainer)}
                onSelection={(value) => {
                    handleSelection(value);
                }}
            >
                <Button
                    className={style.kebabMenuButton}
                    data-testid={testIds.common.primaryView.table.kebabMenu.button}
                >
                    <KebabIcon color={props.theme.iconFillColor} />
                </Button>
                <Menu className={classNames(style.kebabMenu)}>
                    <ul>
                        {!hasTenantCookie() && editVisible && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    value={MENU_ACTIONS.EDIT}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.mcs.table.kebabMenu.editButton}
                                >
                                    {t("Mcs.edit.title")}
                                </MenuItemButton>
                            </li>
                        )}
                        {isActiveOrHalted && !hasTenantCookie() && unregisterVisible && !licenseExpired && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    value={MENU_ACTIONS.UNREGISTER}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.mcs.table.kebabMenu.unregisterButton}
                                >
                                    {t("Mcs.unregister")}
                                </MenuItemButton>
                            </li>
                        )}
                        {!hasTenantCookie() && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    value={MENU_ACTIONS.RESET}
                                    className={classNames(style.menuItem, style.menuItemWithIcon)}
                                    data-testid={testIds.workArea.mcs.table.kebabMenu.resetButton}
                                >
                                    {t("Mcs.reset")}
                                    <Tooltip
                                        content={t("Mcs.resetInfoIconTooltip")}
                                        placement={"right-end"}
                                        popperOptions={{
                                            strategy: "fixed",
                                            modifiers: [{ name: "preventOverflow", enabled: false }],
                                        }}
                                    >
                                        <img src={infoBadge} />
                                    </Tooltip>
                                </MenuItemButton>
                            </li>
                        )}
                        {props.status === MC_STATUS.ACTIVE &&
                            isCurrentUserInternal() &&
                            isInTenantAccess() &&
                            createVisible && (
                                <li className={style.listItem}>
                                    <MenuItemButton
                                        value={MENU_ACTIONS.DISABLE}
                                        className={style.menuItem}
                                        data-testid={testIds.workArea.mcs.table.kebabMenu.disableButton}
                                    >
                                        {t("Mcs.disable")}
                                    </MenuItemButton>
                                </li>
                            )}
                        {props.status === MC_STATUS.DISABLED && isCurrentUserInternal() && isInTenantAccess() && (
                            <li className={style.listItem}>
                                <MenuItemButton
                                    value={MENU_ACTIONS.ENABLE}
                                    className={style.menuItem}
                                    data-testid={testIds.workArea.mcs.table.kebabMenu.enableButton}
                                >
                                    {t("Mcs.enable")}
                                </MenuItemButton>
                            </li>
                        )}
                        {!hideEntitlementCreationAction() && (
                            <li className={style.listItem}>
                                {disableEntitlementCreation() && (
                                    <ReactTooltip
                                        id={entitlementMenuItemId}
                                        className={style.entitlementMenuItemTooltip}
                                        effect={"solid"}
                                    >
                                        {!allowBasedOnEntitlementState()
                                            ? t("Mcs.deliveryBasedOnStateTooltip")
                                            : t("Mcs.deliveryBasedOnSlContainerTooltip")}
                                    </ReactTooltip>
                                )}
                                <MenuItemButton
                                    value={MENU_ACTIONS.DELIVERY}
                                    className={style.menuItem}
                                    onClick={openEntitlementCreationDialog}
                                    data-testid={testIds.workArea.mcs.table.kebabMenu.deliveryButton}
                                    data-for={entitlementMenuItemId}
                                    data-tip
                                    disabled={disableEntitlementCreation()}
                                >
                                    {t("Mcs.delivery")}
                                </MenuItemButton>
                            </li>
                        )}
                    </ul>
                </Menu>
            </Wrapper>
            <Modal isOpen={editMcsFormVisible} hideModal={hideEditMcsForm} modalTitle={t("Mcs.edit.editTitle")}>
                {isLoading ? (
                    <LoadingIndicator />
                ) : (
                    <EditMcsForm
                        name={props.name}
                        uuid={props.uuid}
                        description={props.description}
                        handleCancel={() => setEditMcsFormVisible(false)}
                        handleSubmit={editMc}
                        tableState={props.tableState}
                    />
                )}
            </Modal>

            <Modal isOpen={resultVisible} hideModal={hideResult} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                <div className={style.okButtonContainer}>
                    <button
                        className={buttons.primaryOkButton}
                        onClick={hideResult}
                        data-testid={testIds.common.dialog.closeButton}
                    >
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>

            <ConfirmationModal
                isOpen={resetMcsConfirmationWindowVisible}
                hideModal={toggleResetMcConfirmationModal}
                isLoading={isLoading}
                onConfirm={handleResetClick}
                title={t("Mcs.resetMcConfirmationTitle")}
                subtitle={t("Mcs.resetMcConfirmationSubtitle")}
                actionButtonLabel={t("Mcs.resetMcConfirmationButton")}
            />

            <ConfirmationModal
                isOpen={disableMcsConfirmationModalVisible}
                hideModal={toggleDisableMcConfirmationModal}
                isLoading={isLoading}
                onConfirm={() => toggleMcActiveStatus("disable")}
                title={t("Mcs.disableMcConfirmationTitle")}
                subtitle={"'" + props.name + "'"}
                actionButtonLabel={t("Mcs.disable")}
            />

            <ConfirmationModal
                isOpen={unregisterMcsConfirmationModalVisible}
                hideModal={toggleUnregisterMcConfirmationModal}
                isLoading={isLoading}
                onConfirm={unregisterMc}
                title={t("Mcs.unregisterMcConfirmationTitle")}
                subtitle={t("Mcs.unregisterMcConfirmationSubtitle")}
                actionButtonLabel={t("Mcs.unregister")}
            />
        </div>
    );
};

export default connector(McsKebabMenu);
